import { Button, Spin } from "antd";
import React, { useEffect, useState } from "react";
import Sections from "./Sections";
import AccountManagement from "./components/AccountManagement";
import { EditProfile, EyeInvisible, EyeVisible } from "../../assets/icons";
import { httpService } from "../../base/services/httpService.service";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNotification } from "../../base/hooks/useNotification";
import AuthService from "../../base/services/authentication.service";
import { UserRolesEnum } from "../auth/userRoles.enum";

const listStyle = {
   color: "#3A3737",
   fontSize: "16px",
   fontFamily: "Lato"
};

const emailValidationSchema = Yup.object().shape({
   email: Yup.string()
      .email("Invalid email address")
      .required("Email is required")
});
const passwordValidationSchema = Yup.object().shape({
   oldPassword: Yup.string().required("Current password is required"),
   newPassword: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
         /^(?=.*[A-Z])(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*\d).*$/,
         "Password must contain at least one uppercase letter, one number and one of the specified symbols !, @, #, $, %, ^, &, *"
      )
      .required("New password is required"),
   confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm new password is required")
});
const LoginInfo = () => {
   const { api: notificationApi, notificationContextHolder } = useNotification();
   const [isLoadingEmail, setIsLoadingEmail] = useState(false);
   const [isLoadingPassword, setIsLoadingPassword] = useState(false);
   const [isEditPassword, setIsEditPassword] = useState(false);
   const [isEditEmail, setIsEditEmail] = useState(false);
   const isSuperAdmin = AuthService.isAuthorized([UserRolesEnum.SuperAdmin]);

   const handleEditClick = () => {
      setIsEditPassword(!isEditPassword);
   };
   const handleEditEmail = () => {
      setIsEditEmail(!isEditEmail);
   };

   const handleCancelClick = (resetForm) => {
      resetForm();
      setIsEditPassword(false);
   };
   const handleCancelEmailChange = (resetForm) => {
      resetForm();
      setIsEditEmail(false);
   };

   const [formInitialValues, setFormInitialValues] = useState({
      email: "",
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: ""
   });

   const getEmail = () => {
      httpService.get(
         `Accounts/email`,
         (res) => {
            const data = res?.data;

            setFormInitialValues({
               email: data?.email
            });
         },
         () => {}
      );
   };

   useEffect(() => {
      getEmail();
   }, []);

   const editEmail = async (values, resetForm) => {
      setIsLoadingEmail(true);
      try {
         const updatedValues = {
            ...values
         };

         await httpService.put(
            `Accounts/email`,
            updatedValues,
            (res) => {
               setFormInitialValues(res?.data);
               setIsEditEmail(false);
               resetForm();
               notificationApi.success("user", {
                  message: "Email changed successfully!",
                  description: `Your email was correctly updated.`
               });
            },
            () => {}
         );
         getEmail();
      } catch (error) {
         notificationApi.error("user", {
            message: "Oops! A problem has occurred!",
            description: "We had a problem changing your email, please try again."
         });
      } finally {
         setIsLoadingEmail(false);
      }
   };

   const [error, setError] = useState("");

   const editPassword = async (values, resetForm) => {
      const updatedValues = {
         oldPassword: values?.oldPassword,
         newPassword: values?.newPassword,
         confirmNewPassword: values?.confirmNewPassword
      };

      try {
         setIsLoadingPassword(true);
         await httpService.post(
            `Accounts/password/change`,
            updatedValues,
            () => {
               setIsEditPassword(false);
               resetForm();
               notificationApi.success("request-lock", {
                  message: "Password changed successfully!",
                  description: `Your password was correctly updated.`
               });
               setError("");
            },
            () => {}
         );
      } catch (error) {
         if (
            error.response &&
            error.response.data &&
            error.response.data.errors &&
            error.response.data.errors[0].code === "validation"
         ) {
            const errorMessage = error.response.data.errors[0].message;
            setError(errorMessage);
         } else {
            notificationApi.error("request-lock", {
               message: "Oops! A problem has occurred!",
               description: "We had a problem changing your password, please try again."
            });
         }
      } finally {
         setIsLoadingPassword(false);
      }
   };

   const [passwordVisibility, setPasswordVisibility] = useState({
      oldPassword: "password",
      newPassword: "password",
      confirmNewPassword: "password"
   });

   const [icons, setIcons] = useState({
      oldPassword: EyeInvisible,
      newPassword: EyeInvisible,
      confirmNewPassword: EyeInvisible
   });

   const handleToggle = (field) => {
      setPasswordVisibility((prevState) => ({
         ...prevState,
         [field]: prevState[field] === "password" ? "text" : "password"
      }));

      setIcons((prevIcons) => ({
         ...prevIcons,
         [field]: prevIcons[field] === EyeInvisible ? EyeVisible : EyeInvisible
      }));
   };

   const isStaff = AuthService.isAuthorized([UserRolesEnum.Staff]);
   const isManager = AuthService.isAuthorized([UserRolesEnum.Manager]);

   return (
      <>
         {notificationContextHolder}
         <div
            style={{
               display: "flex",
               flexDirection: "column",
               gap: "40px",
               backgroundColor: "#F5F9FC"
            }}
         >
            <div style={{ padding: "40px 32px", margin: "20px", backgroundColor: "#fff" }}>
               <div className="d-flex justify-content-between">
                  <span
                     style={{
                        color: "#003DAA",
                        fontWeight: 600,
                        fontFamily: "poppins",
                        fontSize: "24px"
                     }}
                  >
                     Login information
                  </span>
               </div>
               <div className="d-flex flex-column" style={{ gap: "40px" }}>
                  <div
                     style={{
                        border: "1.5px solid #DFE6FF",
                        borderRadius: "8px",
                        padding: "32px 24px",
                        backgroundColor: "#fff"
                     }}
                  >
                     <Formik
                        initialValues={formInitialValues}
                        validationSchema={
                           isEditEmail ? emailValidationSchema : passwordValidationSchema
                        }
                        onSubmit={(values, { resetForm }) => {
                           if (isEditEmail) {
                              editEmail(values, resetForm);
                           } else {
                              editPassword(values, resetForm);
                           }
                        }}
                        enableReinitialize
                     >
                        {({
                           handleSubmit,
                           handleChange,
                           touched,
                           values,
                           errors,
                           dirty,
                           setFieldValue,
                           isSubmitting,
                           resetForm
                        }) => (
                           <Form>
                              {isEditPassword ? (
                                 <>
                                    <div className="d-md-flex" style={{ gap: "40px" }}>
                                       <div
                                          className="d-flex flex-column w-100 w-md-50"
                                          style={{ gap: "8px" }}
                                       >
                                          <div
                                             className="d-flex flex-column"
                                             style={{ gap: "40px" }}
                                          >
                                             <div>
                                                <Sections
                                                   fieldType="oldPassword"
                                                   sectionName={"Account information"}
                                                   propertyName={"Current password"}
                                                   propertyValue={values?.oldPassword}
                                                   name="oldPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={"Enter your current password"}
                                                   isRequired={true}
                                                   handleToggle={() => handleToggle("oldPassword")}
                                                   type={passwordVisibility.oldPassword}
                                                   icon={icons.oldPassword}
                                                />

                                                {errors.oldPassword ? (
                                                   <div className="text-danger">
                                                      {errors.oldPassword}
                                                   </div>
                                                ) : (
                                                   error && (
                                                      <div className="text-danger">{error}</div>
                                                   )
                                                )}
                                             </div>
                                             <div>
                                                <Sections
                                                   fieldType="newPassword"
                                                   propertyName={"New password"}
                                                   propertyValue={values?.newPassword}
                                                   name="newPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={"Enter your new password"}
                                                   isRequired={true}
                                                   handleToggle={() => handleToggle("newPassword")}
                                                   type={passwordVisibility.newPassword}
                                                   icon={icons.newPassword}
                                                />
                                                {/* {<div className="text-danger">{error}</div>} */}
                                                {errors.newPassword && (
                                                   <div className="text-danger">
                                                      {errors.newPassword}
                                                   </div>
                                                )}
                                             </div>
                                             <div>
                                                <Sections
                                                   propertyName={"Confirm password"}
                                                   propertyValue={values?.confirmNewPassword}
                                                   name="confirmNewPassword"
                                                   isEditPassword={isEditPassword}
                                                   handleChange={handleChange}
                                                   placeholder={"Enter your new password again"}
                                                   isRequired={true}
                                                   handleToggle={() =>
                                                      handleToggle("confirmNewPassword")
                                                   }
                                                   type={passwordVisibility.confirmNewPassword}
                                                   icon={icons.confirmNewPassword}
                                                />
                                                {errors.confirmNewPassword && (
                                                   <div className="text-danger">
                                                      {errors.confirmNewPassword}
                                                   </div>
                                                )}
                                             </div>
                                          </div>
                                          <div className="footer-buttons flex-wrap">
                                             <Button
                                                className="edit-outlined"
                                                onClick={() => handleCancelClick(resetForm)}
                                                disabled={isLoadingPassword}
                                             >
                                                Cancel
                                             </Button>
                                             <Button
                                                className="edit-button-workspace"
                                                htmlType="submit"
                                                onClick={handleSubmit}
                                                disabled={isLoadingPassword}
                                             >
                                                {isLoadingPassword ? <Spin /> : "Save changes"}
                                             </Button>
                                          </div>
                                       </div>
                                       <div
                                          className="w-100 w-md-50"
                                          style={{
                                             border: "1px solid #DFE6FF",
                                             padding: "32px",
                                             borderRadius: "8px",
                                             backgroundColor: "#FBFDFF",
                                             height: "fit-content"
                                          }}
                                       >
                                          <label
                                             style={{
                                                color: "#3A3737",
                                                fontSize: "16px",
                                                fontFamily: "Lato"
                                             }}
                                          >
                                             In order to protect your account, your password must
                                             contain at least:
                                          </label>
                                          <ul className="d-flex flex-column">
                                             <li style={listStyle}>8 characters long</li>
                                             <li style={listStyle}>One uppercase character</li>
                                             <li style={listStyle}>One lowercase character</li>
                                             <li style={listStyle}>One number</li>
                                             <li style={listStyle}>One special character</li>
                                          </ul>
                                       </div>
                                    </div>
                                 </>
                              ) : isEditEmail ? (
                                 <div
                                    className="d-flex flex-column w-100 w-md-50"
                                    style={{ gap: "8px" }}
                                 >
                                    <div className="w-md-50">
                                       <Sections
                                          sectionName={"Account information"}
                                          propertyName={"Email"}
                                          name="email"
                                          propertyValue={values?.email}
                                          isEditEmail={isEditEmail}
                                          handleChange={handleChange}
                                          isRequired={true}
                                       />
                                       {errors.email && (
                                          <div className="text-danger">{errors.email}</div>
                                       )}
                                    </div>

                                    <div className="footer-buttons flex-wrap w-md-50">
                                       <Button
                                          className="edit-outlined"
                                          onClick={() => handleCancelEmailChange(resetForm)}
                                          disabled={isLoadingEmail}
                                       >
                                          Cancel
                                       </Button>
                                       <Button
                                          className="edit-button-workspace"
                                          htmlType="submit"
                                          disabled={!dirty || isLoadingEmail}
                                          loading={isLoadingEmail}
                                       >
                                          Save changes
                                       </Button>
                                    </div>
                                 </div>
                              ) : (
                                 <div
                                    className="d-md-flex "
                                    style={{
                                       gap: "48px"
                                    }}
                                 >
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                       <Sections
                                          sectionName={"Account information"}
                                          propertyName={"Email"}
                                          propertyValue={values?.email}
                                          isEditEmail={isEditEmail} //test here
                                       />
                                       {!isStaff && !isManager && (
                                          <Button
                                             style={{ height: "32px" }}
                                             icon={<img src={EditProfile} alt="" />}
                                             className="edit-outlined"
                                             onClick={handleEditEmail}
                                          ></Button>
                                       )}
                                    </div>
                                    <div
                                       className="d-flex justify-content-between align-items-center w-100"
                                       style={{ gap: "8px" }}
                                    >
                                       <Sections
                                          propertyName={"Password"}
                                          propertyValue={"You can change your password here"}
                                          isEditPassword={isEditPassword}
                                       />
                                       <Button
                                          style={{ height: "32px" }}
                                          icon={<img src={EditProfile} alt="" />}
                                          className="edit-outlined"
                                          onClick={handleEditClick}
                                       ></Button>
                                    </div>
                                 </div>
                              )}
                           </Form>
                        )}
                     </Formik>
                  </div>
                  {!isSuperAdmin && <AccountManagement />}
               </div>
            </div>
         </div>
      </>
   );
};
export default LoginInfo;
